import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import { useTranslation } from 'react-i18next';
import { useActionSettings } from '../../hooks/useActionSettings';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import TagEditor from '../Widgets/TagEditor';

const ActionsAnonymizePII = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();

  const [sourcePath, setSourcePath] = useState('');
  const [targetPath, setTargetPath] = useState('/{filename}_anonymized.docx');
  const [maskToken, setMaskToken] = useState('[REDACTED_{type}]');
  const [replaceIfExists, setReplaceIfExists] = useState(false);
  const [anonymizeEmails, setAnonymizeEmails] = useState(true);
  const [anonymizePhones, setAnonymizePhones] = useState(true);
  const [anonymizeSsn, setAnonymizeSsn] = useState(true);
  const [anonymizeCreditCards, setAnonymizeCreditCards] = useState(true);
  const [anonymizeIpAddresses, setAnonymizeIpAddresses] = useState(true);
  const [sourceBrowseDisabled, setSourceBrowseDisabled] = useState(false);
  const [targetBrowseDisabled, setTargetBrowseDisabled] = useState(false);

  const sourceRef = useRef(null);
  const targetRef = useRef(null);
  const maskTokenRef = useRef(null);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const availableTags = useMemo(() => {
    if (!tags || !Array.isArray(tags)) {
      return [];
    }

    return tags.map((tag) => ({
      key: tag?.key || tag?.value || tag?.variable || tag?.label,
      label: tag?.label || tag?.key || tag?.value || tag?.variable,
      value: tag?.value || tag?.variable || tag?.key || tag?.label,
      variable: tag?.variable || tag?.value || tag?.key || tag?.label,
      description: tag?.description || '',
      type: tag?.type || 'text',
      reply: tag?.reply || '',
      tag_type: tag?.tag_type || '',
    })).filter((tag) => tag.key && tag.value);
  }, [tags]);

  const {
    isEnabled,
    setIsEnabled,
    isSaveDisabled,
    conditions,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleConditionsChange,
    handleSave: baseHandleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'anonymizepii',
    onSaveStatusChange,
    customFields: {
      sourceFilePath: '',
      targetPath: '/{filename}_anonymized.docx',
      maskToken: '[REDACTED_{type}]',
      replaceIfExists: false,
      anonymizeEmails: true,
      anonymizePhones: true,
      anonymizeSsn: true,
      anonymizeCreditCards: true,
      anonymizeIpAddresses: true,
    },
    customComparison: (initial) => (
      (initial.sourceFilePath || '') === sourcePath &&
      (initial.targetPath || '') === targetPath &&
      (initial.maskToken || '') === maskToken &&
      Boolean(initial.replaceIfExists) === replaceIfExists &&
      Boolean(initial.anonymizeEmails ?? true) === anonymizeEmails &&
      Boolean(initial.anonymizePhones ?? true) === anonymizePhones &&
      Boolean(initial.anonymizeSsn ?? true) === anonymizeSsn &&
      Boolean(initial.anonymizeCreditCards ?? true) === anonymizeCreditCards &&
      Boolean(initial.anonymizeIpAddresses ?? true) === anonymizeIpAddresses
    ),
    customSaveData: () => ({
      sourceFilePath: sourcePath,
      targetPath,
      maskToken,
      replaceIfExists,
      anonymizeEmails,
      anonymizePhones,
      anonymizeSsn,
      anonymizeCreditCards,
      anonymizeIpAddresses,
    }),
    loadDataFunction: (data) => {
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (error) {
          console.error('Failed to parse anonymize pii action data:', error);
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }

      if (parsedData.sourceFilePath !== undefined) {
        setSourcePath(parsedData.sourceFilePath || '');
      }
      if (parsedData.targetPath !== undefined) {
        setTargetPath(parsedData.targetPath || '/{filename}_anonymized.docx');
      }
      if (parsedData.maskToken !== undefined) {
        setMaskToken(parsedData.maskToken || '[REDACTED_{type}]');
      }
      if (parsedData.replaceIfExists !== undefined) {
        setReplaceIfExists(Boolean(parsedData.replaceIfExists));
      }
      if (parsedData.anonymizeEmails !== undefined) {
        setAnonymizeEmails(Boolean(parsedData.anonymizeEmails));
      }
      if (parsedData.anonymizePhones !== undefined) {
        setAnonymizePhones(Boolean(parsedData.anonymizePhones));
      }
      if (parsedData.anonymizeSsn !== undefined) {
        setAnonymizeSsn(Boolean(parsedData.anonymizeSsn));
      }
      if (parsedData.anonymizeCreditCards !== undefined) {
        setAnonymizeCreditCards(Boolean(parsedData.anonymizeCreditCards));
      }
      if (parsedData.anonymizeIpAddresses !== undefined) {
        setAnonymizeIpAddresses(Boolean(parsedData.anonymizeIpAddresses));
      }
    },
    enableConditions: true,
  });

  const { handleBrowseDirectory: handleBrowseSourceFile, FolderBrowserComponent: SourceFileBrowser } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (filePath) => {
      if (filePath) {
        setSourcePath(filePath);
      }
      setSourceBrowseDisabled(false);
    },
    onFolderBrowserClose: () => {
      setSourceBrowseDisabled(false);
    },
  });

  const { handleBrowseDirectory: handleBrowseTargetDirectory, FolderBrowserComponent: TargetFolderBrowser } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    onFolderSelected: (folderPath) => {
      if (!folderPath) {
        return;
      }
      const cleanPath = folderPath.replace(/[\\/]+$/, '');
      const separator = cleanPath.includes('\\') ? '\\' : '/';
      setTargetPath(`${cleanPath}${separator}{filename}_anonymized.docx`);
      setTargetBrowseDisabled(false);
    },
    onFolderBrowserClose: () => {
      setTargetBrowseDisabled(false);
    },
  });

  const handleSave = useCallback(() => {
    baseHandleSave({
      sourceFilePath: sourcePath,
      targetPath,
      maskToken,
      replaceIfExists,
      anonymizeEmails,
      anonymizePhones,
      anonymizeSsn,
      anonymizeCreditCards,
      anonymizeIpAddresses,
      useConditions: true,
      conditions,
    });
  }, [
    anonymizeCreditCards,
    anonymizeEmails,
    anonymizeIpAddresses,
    anonymizePhones,
    anonymizeSsn,
    baseHandleSave,
    conditions,
    maskToken,
    replaceIfExists,
    sourcePath,
    targetPath,
  ]);

  const renderEditorCard = (label, badge, editorRef, value, onChange, placeholder, extraAction = null) => (
    <Card
      sx={{
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #0d6b6f10',
        overflow: 'hidden',
      }}
    >
      <CardHeader
        title={(
          <Typography
            sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              component="span"
              sx={{
                bgcolor: '#0d6b6f',
                color: 'white',
                borderRadius: '6px',
                px: 1.5,
                py: 0.7,
                mr: { xs: 1, sm: 2 },
                fontSize: '0.85rem',
                letterSpacing: '0.5px',
                fontWeight: 600,
              }}
            >
              {badge}
            </Box>
            {label}
          </Typography>
        )}
        sx={{
          borderBottom: '1px solid #f0f4f5',
          backgroundColor: '#f8fbfb',
          p: { xs: 1.5, sm: 2.5 },
        }}
      />
      <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
        <Box
          sx={{
            mb: 1.5,
            border: '1px solid #c6dde0',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#fff',
            '&:focus-within': {
              borderColor: '#0d6b6f',
              boxShadow: '0 0 0 3px rgba(13, 107, 111, 0.1)',
            },
          }}
        >
          <Box sx={{ padding: '12px 16px', minHeight: '28px' }}>
            <TagEditor
              caseid={caseid}
              ref={editorRef}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              singleLine
              tags={availableTags}
            />
          </Box>
        </Box>
        {extraAction}
      </CardContent>
    </Card>
  );

  return (
    <ActionSettingsLayout
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      title={t('workflow.anonymize_pii', 'Anonymize PII')}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={availableTags}
      enableConditions={true}
      popUpOpen={popUpOpen}
      forceRunResult={forceRunResult}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      primaryColor="#0d6b6f"
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          {renderEditorCard(
            t('workflow.source_file_path', 'Source DOCX File'),
            'SOURCE',
            sourceRef,
            sourcePath,
            setSourcePath,
            t('workflow.anonymize_pii.source_placeholder', 'C:\\output\\source.docx'),
            <Button
              variant="contained"
              startIcon={<FolderOpenIcon />}
              onClick={() => {
                setSourceBrowseDisabled(true);
                handleBrowseSourceFile();
              }}
              disabled={sourceBrowseDisabled}
              sx={{ backgroundColor: '#0d6b6f' }}
            >
              {t('workflow.browse', 'Browse')}
            </Button>,
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderEditorCard(
            t('workflow.target_path', 'Target Path'),
            'TARGET',
            targetRef,
            targetPath,
            setTargetPath,
            t('workflow.anonymize_pii.target_placeholder', '/{filename}_anonymized.docx'),
            <Button
              variant="contained"
              startIcon={<FolderOpenIcon />}
              onClick={() => {
                setTargetBrowseDisabled(true);
                handleBrowseTargetDirectory();
              }}
              disabled={targetBrowseDisabled}
              sx={{ backgroundColor: '#0d6b6f' }}
            >
              {t('workflow.browse', 'Browse')}
            </Button>,
          )}
        </Grid>
        <Grid item xs={12}>
          {renderEditorCard(
            t('workflow.anonymize_pii.mask_token_label', 'Mask Token'),
            'MASK',
            maskTokenRef,
            maskToken,
            setMaskToken,
            t('workflow.anonymize_pii.mask_token_placeholder', '[REDACTED_{type}]'),
          )}
        </Grid>
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
              border: '1px solid #0d6b6f10',
            }}
          >
            <CardHeader
              title={(
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontWeight: 700 }}>
                  <PrivacyTipIcon sx={{ color: '#0d6b6f' }} />
                  {t('workflow.anonymize_pii.types_title', 'PII Types to Anonymize')}
                </Typography>
              )}
              subheader={t(
                'workflow.anonymize_pii.types_help',
                'Enable the personal data patterns that should be masked when the DOCX is processed.',
              )}
            />
            <CardContent sx={{ pt: 0 }}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Checkbox checked={anonymizeEmails} onChange={(event) => setAnonymizeEmails(event.target.checked)} />}
                    label={t('workflow.anonymize_pii.emails', 'Email addresses')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Checkbox checked={anonymizePhones} onChange={(event) => setAnonymizePhones(event.target.checked)} />}
                    label={t('workflow.anonymize_pii.phones', 'Phone numbers')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Checkbox checked={anonymizeSsn} onChange={(event) => setAnonymizeSsn(event.target.checked)} />}
                    label={t('workflow.anonymize_pii.ssn', 'Social Security numbers')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Checkbox checked={anonymizeCreditCards} onChange={(event) => setAnonymizeCreditCards(event.target.checked)} />}
                    label={t('workflow.anonymize_pii.credit_cards', 'Credit card numbers')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Checkbox checked={anonymizeIpAddresses} onChange={(event) => setAnonymizeIpAddresses(event.target.checked)} />}
                    label={t('workflow.anonymize_pii.ip_addresses', 'IP addresses')}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
              border: '1px solid #0d6b6f10',
            }}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PrivacyTipIcon sx={{ color: '#0d6b6f' }} />
              <Box sx={{ flex: 1 }}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={replaceIfExists}
                      onChange={(event) => setReplaceIfExists(event.target.checked)}
                    />
                  )}
                  label={t(
                    'workflow.anonymize_pii.replace_if_exists',
                    'Replace target file if it already exists',
                  )}
                />
                <Typography variant="body2" color="text.secondary">
                  {t(
                    'workflow.anonymize_pii.replace_if_exists_help',
                    'When enabled, the anonymized DOCX will overwrite an existing file at the target path.',
                  )}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {SourceFileBrowser}
      {TargetFolderBrowser}
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsAnonymizePII);
